import api from '../utils/api';

const DEFAULT_TIMEOUT = 30000; // 30 seconds

// Use centralized api instance for auth headers and interceptors

// Configure timeout for image uploads (120 seconds)
const IMAGE_UPLOAD_TIMEOUT = 120000; // 120 seconds

// Sleep helper to avoid inline functions in loops capturing changing variables
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Token handling is centralized in api.js

// Helper function to handle API errors
const handleApiError = (error, context) => {
  console.error(`API Error in ${context}:`, error);
  
  // Log request details if available
  if (error.config) {
    console.log('Request details:', {
      url: error.config.url,
      method: error.config.method,
      timeout: error.config.timeout,
      headers: {
        ...error.config.headers,
        Authorization: error.config.headers?.Authorization ? '[REDACTED]' : undefined,
        'x-auth-token': error.config.headers?.['x-auth-token'] ? '[REDACTED]' : undefined
      }
    });
  }
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
    
    if (error.response.status === 400) {
      // Handle 400 Bad Request errors
      const errorDetails = error.response.data?.details || '';
      const errorMessage = error.response.data?.message || error.response.data?.error || 'Bad Request: The server could not process the request';
      
      // Log validation errors if present
      if (error.response.data?.validationErrors) {
        console.error('Validation errors:', error.response.data.validationErrors);
      }
      
      const fullErrorMessage = errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage;
      throw new Error(fullErrorMessage);
    } else if (error.response.status === 401) {
      // Handle authentication errors
      localStorage.removeItem('token'); // Clear invalid token
      throw new Error('Your session has expired. Please log in again.');
    } else if (error.response.status === 403) {
      // Handle authorization errors
      throw new Error('You do not have permission to perform this action.');
    } else if (error.response.status === 409) {
      // Handle conflict errors (e.g., duplicate scene/shot numbers)
      const suggestedShotNumber = error.response.data?.suggestedShotNumber;
      const errorObj = new Error(error.response.data?.message || 'A conflict occurred with existing data');
      errorObj.suggestedShotNumber = suggestedShotNumber;
      throw errorObj;
    } else if (error.response.status === 413) {
      // Handle payload too large errors
      throw new Error('The image is too large. Please reduce the image size and try again (maximum 10MB).');
    } else if (error.response.status >= 500) {
      // Handle server errors
      throw new Error('Server error: The server encountered an error processing your request. Please try again later.');
    } else {
      throw new Error(error.response.data?.message || error.response.data?.error || error.message || 'An unexpected error occurred');
    }
  } else if (error.request) {
    // The request was made but no response was received
    console.error('No response received. Request:', error.request);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. The server took too long to respond, possibly due to a large image size. Please try again with a smaller image.');
    }
    
    throw new Error('No response received from server. Please check your network connection.');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error setting up request:', error.message);
    throw new Error(`Error setting up request: ${error.message}`);
  }
};

export const getProjectStoryboards = async (projectId) => {
  try {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const response = await api.get(`/projects/${projectId}/storyboards`, {
      timeout: DEFAULT_TIMEOUT
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    // Debug: Log what's coming back from server
    console.log('Storyboards from server:', response.data.map(s => ({
      id: s._id?.slice(-6),
      hasPreviewUrl: !!s.previewUrl,
      previewUrlLength: s.previewUrl?.length || 0,
      imageUrl: s.imageUrl?.substring(0, 50)
    })));

    // Ensure a stable, predictable order from the client side as a safety net
    const data = Array.isArray(response.data) ? [...response.data] : response.data;
    if (Array.isArray(data)) {
      data.sort((a, b) => {
        const ao = typeof a.order === 'number' ? a.order : 0;
        const bo = typeof b.order === 'number' ? b.order : 0;
        return ao - bo;
      });
    }

    return data;
  } catch (error) {
    return handleApiError(error, 'getProjectStoryboards');
  }
};

export const createStoryboard = async (projectId, storyboardData) => {
  try {
    // Input validation
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    if (!storyboardData) {
      throw new Error('Storyboard data is required');
    }
    if (!storyboardData.imageUrl) {
      throw new Error('Image is required');
    }

    // Create a copy of the data to avoid mutating the original
    const dataToSend = { ...storyboardData };

    // Ensure required fields are present
    dataToSend.sceneNumber = parseInt(dataToSend.sceneNumber) || 1;
    dataToSend.shotNumber = parseInt(dataToSend.shotNumber) || 1;
    dataToSend.order = parseInt(dataToSend.order) || 0;
    dataToSend.description = dataToSend.description || '';
    dataToSend.aspectRatio = dataToSend.aspectRatio || '1.78';
    dataToSend.duration = parseFloat(dataToSend.duration) || 0;
    dataToSend.cameraAngle = dataToSend.cameraAngle || 'Other';
    dataToSend.movement = dataToSend.movement || 'Static';
    dataToSend.notes = dataToSend.notes || '';
    dataToSend.imagePosition = {
      x: Number(dataToSend.imagePosition?.x || 0),
      y: Number(dataToSend.imagePosition?.y || 0)
    };
    dataToSend.imageScale = Number(dataToSend.imageScale || 1);
    dataToSend.drawings = Array.isArray(dataToSend.drawings) ? dataToSend.drawings : [];

    // Handle image data
    const imageUrl = dataToSend.imageUrl;
    delete dataToSend.imageUrl; // Remove imageUrl as we'll use image object instead

    if (imageUrl.startsWith('data:')) {
      // It's a base64 image
      const matches = imageUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
      if (!matches || matches.length !== 3) {
        throw new Error('Invalid image data format');
      }
      const [, mimeType, base64Data] = matches;
      if (!mimeType.startsWith('image/')) {
        throw new Error('Invalid image type. Only images are allowed.');
      }
      
      // Validate base64 data
      try {
        // Check if base64 data is valid
        const decoded = atob(base64Data);
        if (!decoded || decoded.length === 0) {
          throw new Error('Invalid or empty image data');
        }
        
        // Check if image size is within limits (10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const approxSize = Math.ceil(base64Data.length * 0.75);
        if (approxSize > maxSize) {
          throw new Error(`Image size exceeds maximum limit of 10MB. Current size: ${(approxSize / (1024 * 1024)).toFixed(2)}MB`);
        }
      } catch (e) {
        console.error('Base64 validation error:', e);
        throw new Error(`Invalid image data: ${e.message}`);
      }

      // Set image data in the format expected by the server
      dataToSend.image = {
        data: base64Data,
        contentType: mimeType
      };
    } else {
      // For external URLs, we'll need to fetch and convert to base64
      throw new Error('External image URLs are not supported. Please upload an image file.');
    }

    console.log('Creating storyboard with data:', {
      projectId,
      sceneNumber: dataToSend.sceneNumber,
      shotNumber: dataToSend.shotNumber,
      description: dataToSend.description,
      order: dataToSend.order,
      aspectRatio: dataToSend.aspectRatio,
      hasImage: true,
      imageType: dataToSend.image.contentType,
      imageDataLength: dataToSend.image.data ? dataToSend.image.data.length : 0,
      approximateSizeMB: dataToSend.image.data ? (dataToSend.image.data.length * 0.75 / (1024 * 1024)).toFixed(2) : 0
    });

    try {
      const response = await api.post(
        `/projects/${projectId}/storyboards`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: IMAGE_UPLOAD_TIMEOUT // Extended timeout for large images
        }
      );

      return response.data;
    } catch (apiError) {
      return handleApiError(apiError, 'createStoryboard');
    }
  } catch (error) {
    console.error('Error creating storyboard:', error);
    throw error;
  }
};

export const updateStoryboard = async (projectId, storyboardData) => {
  try {
    if (!projectId || !storyboardData || !storyboardData._id) {
      throw new Error('Project ID and storyboard data with ID are required');
    }

    // Create a copy of the data to avoid mutating the original
    const dataToSend = { ...storyboardData };
    const storyboardId = dataToSend._id;

    // Handle image data if present
    if (dataToSend.imageUrl && dataToSend.imageUrl.startsWith('data:')) {
      // It's a base64 image
      const matches = dataToSend.imageUrl.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
      if (!matches || matches.length !== 3) {
        throw new Error('Invalid image data format');
      }
      const [, mimeType, base64Data] = matches;
      
      // Validate base64 data
      try {
        // Check if base64 data is valid
        const decoded = atob(base64Data);
        if (!decoded || decoded.length === 0) {
          throw new Error('Invalid or empty image data');
        }
        
        // Check if image size is within limits (10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const approxSize = Math.ceil(base64Data.length * 0.75);
        if (approxSize > maxSize) {
          throw new Error(`Image size exceeds maximum limit of 10MB. Current size: ${(approxSize / (1024 * 1024)).toFixed(2)}MB`);
        }
      } catch (e) {
        console.error('Base64 validation error:', e);
        throw new Error(`Invalid image data: ${e.message}`);
      }

      // Set image data in the format expected by the server
      dataToSend.image = {
        data: base64Data,
        contentType: mimeType
      };
    }
    
    // Remove imageUrl as we'll use image object instead
    delete dataToSend.imageUrl;

    console.log('Updating storyboard with data:', {
      projectId,
      storyboardId,
      hasImage: !!dataToSend.image,
      imageType: dataToSend.image?.contentType,
      imageDataLength: dataToSend.image?.data ? dataToSend.image.data.length : 0,
      hasPreviewUrl: !!dataToSend.previewUrl,
      previewUrlLength: dataToSend.previewUrl ? dataToSend.previewUrl.length : 0
    });

    try {
      const response = await api.put(
        `/projects/${projectId}/storyboards/${storyboardId}`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: IMAGE_UPLOAD_TIMEOUT // Extended timeout for large images
        }
      );

      return response.data;
    } catch (apiError) {
      return handleApiError(apiError, 'updateStoryboard');
    }
  } catch (error) {
    console.error('Error updating storyboard:', error);
    throw error;
  }
};

export const deleteStoryboard = async (projectId, storyboardId) => {
  try {
    if (!projectId || !storyboardId) {
      throw new Error('Project ID and storyboard ID are required');
    }

    const response = await api.delete(
      `/projects/${projectId}/storyboards/${storyboardId}`,
      { timeout: DEFAULT_TIMEOUT }
    );

    return response.data;
  } catch (error) {
    return handleApiError(error, 'deleteStoryboard');
  }
};

export const reorderStoryboards = async (projectId, reorderData) => {
  try {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    // Ensure frameIds is an array
    if (!reorderData.frameIds || !Array.isArray(reorderData.frameIds)) {
      throw new Error('Frame IDs must be provided as an array');
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Reordering storyboards:', {
        projectId,
        sourceIndex: reorderData.sourceIndex,
        destinationIndex: reorderData.destinationIndex,
        frameCount: reorderData.frameIds?.length
      });
    }

    // Add retry logic with exponential backoff
    let retries = 0;
    const maxRetries = 3;
    let lastError = null;
    
    while (retries < maxRetries) {
      try {
        const response = await api.put(
          `/projects/${projectId}/storyboards/reorder`,
          reorderData,
          {
            timeout: DEFAULT_TIMEOUT + (retries * 5000) // Increase timeout with each retry
          }
        );

        // Validate that the response contains the expected data
        if (!response.data || !Array.isArray(response.data)) {
          throw new Error('Invalid response format from server');
        }

        // Verify that all frameIds are present in the response
        const responseIds = response.data.map(frame => frame._id);
        const missingIds = reorderData.frameIds.filter(id => !responseIds.includes(id));
        
        if (missingIds.length > 0) {
          throw new Error(`Response missing ${missingIds.length} frame IDs`);
        }

        // Verify that order values are set correctly
        const hasOrderValues = response.data.every(frame => typeof frame.order === 'number');
        if (!hasOrderValues) {
          throw new Error('Some frames are missing order values');
        }

        return response.data;
      } catch (err) {
        lastError = err;
        retries++;
        if (retries < maxRetries) {
          // Wait with exponential backoff before retrying
          const delay = 1000 * Math.pow(2, retries);
          await sleep(delay);
          if (process.env.NODE_ENV === 'development') {
            console.log(`Retrying reorderStoryboards (${retries}/${maxRetries})...`);
          }
        }
      }
    }
    
    // If we've exhausted all retries, throw the last error
    throw lastError || new Error('Failed to reorder storyboards after multiple attempts');
  } catch (error) {
    return handleApiError(error, 'reorderStoryboards');
  }
};

export const deleteFrame = async (projectId, storyboardId, frameId) => {
  try {
    if (!projectId || !storyboardId || !frameId) {
      throw new Error('Project ID, storyboard ID, and frame ID are required');
    }

    const response = await api.delete(
      `/projects/${projectId}/storyboards/${storyboardId}/frames/${frameId}`,
      { timeout: DEFAULT_TIMEOUT }
    );

    return response.data;
  } catch (error) {
    return handleApiError(error, 'deleteFrame');
  }
};
